import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { ktmToday } from '../../utils/calendar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdAdd, MdClose } from 'react-icons/md';
import DualDatePicker from '../common/DualDatePicker';
import EditDetailsModal, { Field, inputCls } from '../common/EditDetailsModal';
import { updateTrainer } from '../../services/trainerService';
import { sanitizePhone, isValidPhone, PHONE_HINT } from '../../utils/inputRules';

const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

// Admin "Edit Details" for a trainer. Pre-filled; PATCHes personal + professional fields only
// (login email is Phase 2; salary/status/notes keep their own gated flows). Certifications preserve
// any untouched sub-fields (issue/expiry dates, document URL) by merging back onto the originals.
export default function EditTrainerModal({ trainer, onClose, onSaved }) {
  const qc = useQueryClient();
  const originalCerts = trainer.certifications || [];
  const hasLogin = !!(trainer.user?.email && !trainer.user.email.includes('@noemail.club'));

  const { register, control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name:            trainer.fullName || trainer.user?.name || '',
      phone:           trainer.phone || '',
      dateOfBirth:     isoDay(trainer.dateOfBirth),
      gender:          trainer.gender || '',
      address: {
        street:   trainer.address?.street   || '',
        city:     trainer.address?.city     || '',
        district: trainer.address?.district || '',
        province: trainer.address?.province || '',
      },
      specializations: (trainer.specializations || []).join(', '),
      bio:             trainer.bio || '',
      experienceYears: trainer.experienceYears ?? '',
      emergencyContact: {
        name:         trainer.emergencyContact?.name         || '',
        phone:        trainer.emergencyContact?.phone        || '',
        relationship: trainer.emergencyContact?.relationship || '',
      },
      certifications: originalCerts.map(c => ({ name: c.name || '', issuedBy: c.issuedBy || '' })),
    },
  });
  const certs = useFieldArray({ control, name: 'certifications' });

  const mutation = useMutation({
    mutationFn: (payload) => updateTrainer(trainer._id, payload),
    onSuccess: () => {
      toast.success('Details updated');
      qc.invalidateQueries({ queryKey: ['trainer-full', trainer._id] });
      qc.invalidateQueries({ queryKey: ['trainers'] });
      onSaved?.();
      onClose?.();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update details'),
  });

  const onSubmit = (vals) => {
    mutation.mutate({
      name:            vals.name?.trim(),
      phone:           vals.phone?.trim() || '',
      dateOfBirth:     vals.dateOfBirth || '',
      gender:          vals.gender || '',
      address:         vals.address,
      specializations: (vals.specializations || '').split(',').map(s => s.trim()).filter(Boolean),
      bio:             vals.bio?.trim() || '',
      experienceYears: vals.experienceYears === '' ? 0 : Number(vals.experienceYears),
      emergencyContact: vals.emergencyContact,
      // Preserve issue/expiry dates + document URL on existing rows; keep only named entries.
      certifications: (vals.certifications || [])
        .map((c, i) => ({ ...(originalCerts[i] || {}), name: c.name?.trim(), issuedBy: c.issuedBy?.trim() }))
        .filter(c => c.name),
    });
  };

  return (
    <EditDetailsModal
      title="Edit Trainer"
      subtitle="Update personal and professional details."
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      saving={mutation.isPending}
      hasLogin={hasLogin}
    >
      {/* Identity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name" required error={errors.name?.message}>
          <input className={inputCls(errors.name)}
            {...register('name', { required: 'Name is required', maxLength: { value: 50, message: 'At most 50 characters' } })} />
        </Field>
        <Field label="Phone" error={errors.phone?.message}>
          <input type="tel" placeholder="97/98XXXXXXXX" className={inputCls(errors.phone)}
            {...register('phone', { validate: (val) => !val || isValidPhone(val) || PHONE_HINT })}
            onChange={(e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true })} />
        </Field>
        <Controller name="dateOfBirth" control={control}
          render={({ field }) => (
            <DualDatePicker label="Date of Birth" value={field.value || ''} onChange={field.onChange}
              max={ktmToday()} fastNav />
          )} />
        <Field label="Gender">
          <select className={inputCls()} {...register('gender')}>
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </Field>
        <Field label="Experience (years)">
          <input type="number" min="0" className={inputCls()} {...register('experienceYears')} />
        </Field>
      </div>

      {/* Professional */}
      <Field label="Specializations (comma-separated)">
        <input placeholder="e.g. Taekwondo, Kickboxing" className={inputCls()} {...register('specializations')} />
      </Field>
      <Field label="Bio">
        <textarea rows={2} placeholder="Short bio shown on the public trainer profile"
          className={`${inputCls()} resize-none`} {...register('bio', { maxLength: { value: 500, message: 'At most 500 characters' } })} />
      </Field>

      {/* Address */}
      <div className="pt-1">
        <p className="text-xs font-semibold text-muted mb-2">Address</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Street"><input className={inputCls()} {...register('address.street')} /></Field>
          <Field label="City"><input className={inputCls()} {...register('address.city')} /></Field>
          <Field label="District"><input className={inputCls()} {...register('address.district')} /></Field>
          <Field label="Province"><input className={inputCls()} {...register('address.province')} /></Field>
        </div>
      </div>

      {/* Emergency contact */}
      <div className="pt-1">
        <p className="text-xs font-semibold text-muted mb-2">Emergency Contact</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Name"><input className={inputCls()} {...register('emergencyContact.name')} /></Field>
          <Field label="Phone">
            <input type="tel" className={inputCls()} {...register('emergencyContact.phone')}
              onChange={(e) => setValue('emergencyContact.phone', sanitizePhone(e.target.value))} />
          </Field>
          <Field label="Relationship"><input className={inputCls()} {...register('emergencyContact.relationship')} /></Field>
        </div>
      </div>

      {/* Certifications (name + issuer; dates/document preserved) */}
      <div className="pt-1">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-muted">Certifications</p>
          {certs.fields.length < 20 && (
            <button type="button" onClick={() => certs.append({ name: '', issuedBy: '' })}
              className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline">
              <MdAdd className="text-sm" /> Add
            </button>
          )}
        </div>
        {certs.fields.length === 0 && <p className="text-xs text-subtle">None added.</p>}
        <div className="space-y-3">
          {certs.fields.map((f, i) => (
            <div key={f.id} className="relative bg-nav-hover/40 border border-divider rounded-xl p-3">
              <button type="button" onClick={() => certs.remove(i)}
                className="absolute top-2 right-2 p-1 text-subtle hover:text-red-400 rounded-lg" aria-label="Remove">
                <MdClose className="text-sm" />
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Certificate"><input className={inputCls()} {...register(`certifications.${i}.name`)} /></Field>
                <Field label="Issued by"><input className={inputCls()} {...register(`certifications.${i}.issuedBy`)} /></Field>
              </div>
            </div>
          ))}
        </div>
      </div>
    </EditDetailsModal>
  );
}
